import React, { useEffect, useState } from "react";
import {
  useNavigate,
  useParams
} from "react-router-dom";
import {
  ArrowLeft,
  Save
} from "lucide-react";
import * as ownerApi from "../../api/payroll/payPeriodApi";
import * as portalApi from "../../api/payroll/payPeriodPortalApi";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  Checkbox
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

export default function PayPeriodForm({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const base = isPortal ? "/staff-panel/pay-periods" : "/pay-periods";
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState({
    name: "",
    type: "MONTHLY",
    endDay: "",
    isDefault: false,
    active: true,
  });
  const [loading, setLoading] = useState(false);

  const toast = useToast();

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await api.getAllPayPeriods();
        const list = Array.isArray(res) ? res : res?.data || [];
        const found = list.find((x) => x._id === id);
        if (found) {
          setForm({
            name: found.name || "",
            type: found.type || "MONTHLY",
            endDay: found.endDay ?? "",
            isDefault: !!found.isDefault,
            active: found.active !== false,
          });
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Load failed");
      }
    })();
  }, [id, isPortal, api]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        endDay: form.endDay === "" ? null : Number(form.endDay),
      };
      if (id) await api.updatePayPeriod(id, payload);
      else await api.createPayPeriod(payload);
      navigate(base);
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const periodTypeOptions = [
    { label: "Monthly", value: "MONTHLY" },
    { label: "Semi-Monthly", value: "SEMI_MONTHLY" },
    { label: "Bi-Weekly", value: "BI_WEEKLY" },
    { label: "Weekly", value: "WEEKLY" },
  ];

  return (
    <div className="mx-auto max-w-xl space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft className="h-3.5 w-3.5" />}
          onClick={() => navigate(base)}
          className="mb-2 -ml-2"
        >
          Back to Periods
        </Button>
        <h1 className="text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
          {id ? "Edit" : "Create"} Pay Period
        </h1>
      </div>

      

      <Card>
        <CardHeader className="bg-neutral-50/50">
          <CardTitle>Pay Period Details</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={submit} className="space-y-4">
            <Input
              label="Period Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Standard Calendar Month"
              required
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Select
                label="Period Type"
                options={periodTypeOptions}
                value={form.type}
                onChange={(val) => setForm({ ...form, type: val })}
              />

              <Input
                label="Cut-off / End Day (optional)"
                type="number"
                min="1"
                max="31"
                value={form.endDay}
                onChange={(e) => setForm({ ...form, endDay: e.target.value })}
                placeholder="e.g. 25 for 26th–25th cycle"
              />
            </div>

            <div className="space-y-3 pt-2">
              <Checkbox
                id="isDefault"
                checked={form.isDefault}
                onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                label="Set as default pay period"
              />

              <Checkbox
                id="active"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                label="Active Period"
              />
            </div>

            <div className="flex items-center gap-3 border-t border-neutral-200 dark:border-neutral-800 pt-4">
              <Button
                type="submit"
                isLoading={loading}
                variant="primary"
                leftIcon={<Save className="h-4 w-4" />}
              >
                {id ? "Update Period" : "Create Period"}
              </Button>
              <Button type="button" onClick={() => navigate(base)} variant="ghost">
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}